/**
 * Tiny reactive store. Avoids pulling a framework just for state.
 * Pattern: signals + selectors. Use sparingly — the configurator state is small.
 */

import type { Listener } from "@/types";

export class Signal<T> {
  private listeners: Listener<T>[] = [];
  constructor(private value: T) {}

  get(): T {
    return this.value;
  }

  set(next: T | ((prev: T) => T)): void {
    const newValue =
      typeof next === "function" ? (next as (prev: T) => T)(this.value) : next;
    if (Object.is(newValue, this.value)) return;
    this.value = newValue;
    this.emit();
  }

  /** Subscribe; returns an unsubscribe fn. */
  subscribe(listener: Listener<T>): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(): void {
    for (const l of this.listeners) {
      try {
        l(this.value);
      } catch (e) {
        console.error("Signal listener failed", e);
      }
    }
  }
}

/** Convenience: derive a signal from another via a transform fn. */
export function derive<A, B>(src: Signal<A>, fn: (a: A) => B): Signal<B> {
  const out = new Signal<B>(fn(src.get()));
  src.subscribe((next) => out.set(fn(next)));
  return out;
}
